import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {GdprTerms} from "../models/gdpr-terms.model";

/**
 * Serwis warstwy danych zarządzający zgodnością prawną (GDPR).
 * Obsługuje cykl życia umów powierzenia danych: od pobrania szablonu,
 * przez akceptację, aż po generowanie podpisanych dokumentów PDF.
 */
@Injectable({
  providedIn: 'root'
})
export class GdprTermsService {

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils,
  ) {
  }

  /**
   * Pobiera surowy tekst domyślnego szablonu umowy GDPR.
   *
   * @returns Observable emitujący treść szablonu (string).
   */
  getTemplate(): Observable<string> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/gdpr-template').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.value;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera zawartość tekstową (treść) aktualnie obowiązującej umowy GDPR
   * dla wskazanego klienta.
   *
   * @param vClientUUID Identyfikator klienta.
   * @returns Observable emitujący tekst zaakceptowanej umowy.
   */
  getActiveAgreement(vClientUUID: string): Observable<string> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/gdpr/${vClientUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.value;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera pełen, zinstancjonowany obiekt warunków GDPR dla konkretnego klienta.
   * Obiekt ten zawiera metadane, takie jak daty akceptacji czy wersje dokumentu.
   *
   * @param vClientUUID Identyfikator klienta.
   * @returns Observable emitujący obiekt GdprTerms.
   */
  getActiveTerms(vClientUUID: string): Observable<GdprTerms> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/gdpr/${vClientUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<GdprTerms>(new GdprTerms(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Rejestruje prawną akceptację warunków GDPR w systemie (tzw. zgoda audytowalna).
   *
   * @param gdrpToAccept Obiekt zawierający warunki oraz metadane potwierdzające akceptację.
   * @returns Observable emitujący odpowiedź serwera potwierdzającą zapisanie zgody.
   */
  acceptTerms(gdrpToAccept: GdprTerms) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/gdpr-accept', gdrpToAccept).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wycofuje lub unieważnia wcześniej zaakceptowaną umowę powierzenia danych.
   *
   * @param gdprUUID Identyfikator konkretnej umowy/zgody.
   * @returns Observable emitujący status operacji z serwera.
   */
  cancelTerms(gdprUUID: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/gdpr/cancel/${gdprUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Odpytuje serwer o aktualny, dokładny czas.
   * W kontekście RODO jest to mechanizm zabezpieczający –
   * gwarantuje, że data i godzina akceptacji umowy przypisana przez frontend
   * nie jest zafałszowana przez przestawiony zegar w systemie operacyjnym klienta.
   *
   * @returns Observable emitujący czas serwera.
   */
  getServerTime() {
    return this.apiService.get<VetineoApiResponse>('/dashboard/get-server-time').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.currentServerTime;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Generuje i pobiera bezpieczny, tymczasowy link URL do fizycznego pliku PDF
   * z zawartą umową powierzenia danych.
   * Chroni dokumenty prawne przed nieautoryzowanym pobraniem z zewnątrz.
   *
   * @param vClientUUID Identyfikator klienta.
   * @param gdprUUID Identyfikator umowy.
   * @returns Observable emitujący wygenerowany URL z tokenem autoryzacyjnym.
   */
  printGdprPdf(vClientUUID: string, gdprUUID: string): Observable<string> {
    return this.apiService.get<VetineoApiResponse>(`/storage/get-signed-url-for-gdpr/${vClientUUID}/${gdprUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response.signedUrl) {
        return vetineoApiResponse.response.signedUrl
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera warunki RODO na poziomie konta indywidualnego.
   *
   * @returns Observable emitujący warunki dla konta indywidualnego.
   */
  getIndividualTerms(): Observable<GdprTerms>  {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/individual-gdpr`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<GdprTerms>(new GdprTerms(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }
}
